/** Shared Azure Queue message types and service bounds (renderer + main). */
export const QUEUE_PEEK_MAX = 32;
export const QUEUE_RECEIVE_VISIBILITY_MIN_SECONDS = 1;
export const QUEUE_SEND_VISIBILITY_MIN_SECONDS = 0;
export const QUEUE_VISIBILITY_MAX_SECONDS = 604_800;
export const QUEUE_DEFAULT_RECEIVE_VISIBILITY_SECONDS = 30;
export const QUEUE_TTL_INFINITE = -1;
export const QUEUE_TTL_MIN_SECONDS = 1;
export const QUEUE_TTL_DEFAULT_SECONDS = 604_800;
export const QUEUE_TTL_FORM_MAX_SECONDS = 2_147_483_647;
export const QUEUE_MESSAGE_MAX_BYTES = 64 * 1024;
export const QUEUE_NAME_RULES = { min: 3, max: 63, pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/ } as const;
/** Live UI receipts main keeps per connection; a Receive that would need more is refused before dispatch. */
export const QUEUE_RECEIPTS_PER_CONNECTION_MAX = 200;
/** Receipt handles one view may send with a peek for revalidation (the budget plus one in-flight batch). */
export const QUEUE_VIEW_RECEIPT_HANDLES_MAX = QUEUE_RECEIPTS_PER_CONNECTION_MAX + QUEUE_PEEK_MAX;

export type StorageQueueMessage = {
  messageId: string;
  insertedOn?: string;
  expiresOn?: string;
  dequeueCount?: number;
  text: string;
};

/** The handle is opaque; the Azure pop receipt remains in the main process. */
export type StorageQueueReceivedMessage = StorageQueueMessage & {
  receiptHandle: string;
  /** LOCAL-clock deadline (`now + visibility` when main got the answer); every receipt gate compares it with `Date.now()`. */
  visibleUntil: string;
  /** Azure's own "visible again" time (service clock) — display only; never compared with the local clock. */
  nextVisibleOn?: string;
  /** Azure's expiry time (service clock) — display only when `expiryDeadline` is present. */
  expiresOn?: string;
  /**
   * LOCAL-clock expiry deadline, derived when main got the answer: `visibleUntil + (expiresOn − nextVisibleOn)`
   * (the difference of two service timestamps is clock-free). Every expiry gate compares it with `Date.now()`;
   * absent when the service omitted either timestamp, and the gates then fall back to `expiresOn` (review E R1).
   */
  expiryDeadline?: string;
};

export type QueueReceiptState = 'valid' | 'expiring' | 'stale' | 'expired' | 'lost';
export type QueueOperation = 'list' | 'peek' | 'send' | 'receive' | 'update' | 'message-delete' | 'clear' | 'create' | 'remove';
export type QueueOperationPolicy = {
  kind: 'read' | 'mutation';
  mcpClass: 'read' | 'write' | 'none';
  retryClass: 'idempotent-read' | 'queue-mutation';
};

const parsedTime = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** The expiry instant a gate compares with the local clock: the local deadline when known, else Azure's value. */
export const queueExpiryDeadline = (record: { expiresOn?: string; expiryDeadline?: string }): string | undefined => record.expiryDeadline ?? record.expiresOn;

export function queueReceiptState(
  row: { visibleUntil: string; expiresOn?: string; expiryDeadline?: string; lost?: boolean },
  now: number,
  options: { warnWithinMs?: number } = {},
): QueueReceiptState {
  if (row.lost) return 'lost';
  const visibleUntil = parsedTime(row.visibleUntil);
  const expiry = queueExpiryDeadline(row);
  const expiresOn = parsedTime(expiry);
  if (visibleUntil === undefined || (expiry !== undefined && expiresOn === undefined)) return 'expired';
  if (expiresOn !== undefined && now >= expiresOn) return 'expired';
  if (now >= visibleUntil) return 'stale';
  return visibleUntil - now <= (options.warnWithinMs ?? 10_000) ? 'expiring' : 'valid';
}

export function queueOperationPolicy(op: QueueOperation): QueueOperationPolicy {
  switch (op) {
    case 'list':
    case 'peek':
      return { kind: 'read', mcpClass: 'read', retryClass: 'idempotent-read' };
    case 'send':
    case 'receive':
    case 'clear':
      return { kind: 'mutation', mcpClass: 'write', retryClass: 'queue-mutation' };
    case 'update':
    case 'message-delete':
    case 'create':
    case 'remove':
      return { kind: 'mutation', mcpClass: 'none', retryClass: 'queue-mutation' };
    default: {
      const exhaustive: never = op;
      return exhaustive;
    }
  }
}

export function maxUpdateVisibilitySeconds(record: { expiresOn?: string; expiryDeadline?: string }, now: number): number {
  const expiry = queueExpiryDeadline(record);
  if (expiry === undefined) return QUEUE_VISIBILITY_MAX_SECONDS;
  const expiresOn = parsedTime(expiry);
  if (expiresOn === undefined) return 0;
  return Math.max(0, Math.min(QUEUE_VISIBILITY_MAX_SECONDS, Math.floor((expiresOn - now) / 1000)));
}
